import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { ClipboardList, Clock, Play, Users, User, ChevronRight } from "lucide-react";

const MODE_LABELS = { self_paced: "Self-paced", live_group: "Live group", one_to_one: "One-to-one" };
const MODE_ICONS = { self_paced: Play, live_group: Users, one_to_one: User };

/** Home widget: shows the most recent care-plan a doctor prescribed on any
 * of this patient's bookings. Renders 1-5 therapy chips + doctor's note. */
export function CarePlanCard() {
  const [plan, setPlan] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/bookings").then((r) => {
      // find newest booking with a prescription
      const withPlan = r.data
        .filter((b) => b.prescribed_at && Array.isArray(b.prescribed_therapies) && b.prescribed_therapies.length)
        .sort((a, b) => (b.prescribed_at || "").localeCompare(a.prescribed_at || ""));
      if (withPlan[0]) setPlan(withPlan[0]);
    }).catch(() => {});
  }, []);

  if (!plan) return null;

  return (
    <section
      className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6 lg:p-7"
      data-testid="home-care-plan"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="uppercase-label inline-flex items-center gap-1">
            <ClipboardList size={14} /> Your care plan for this week
          </span>
          <div className="text-xs text-[color:var(--mano-ink-soft)] mt-1">
            Prescribed {plan.prescribed_at ? new Date(plan.prescribed_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : ""}
          </div>
        </div>
      </div>

      {plan.prescribed_note && (
        <div className="mt-4 rounded-xl bg-[color:var(--mano-primary)]/5 border border-[color:var(--mano-primary)]/20 text-[color:var(--mano-ink)] p-3 text-sm italic">
          "{plan.prescribed_note}"
        </div>
      )}

      <div className="mt-4 space-y-2">
        {plan.prescribed_therapies.map((t) => {
          const Icon = MODE_ICONS[t.mode] || Play;
          return (
            <button
              key={t.id}
              onClick={() => nav("/therapies")}
              data-testid={`careplan-item-${t.id}`}
              className="w-full text-left rounded-xl border border-[color:var(--mano-border)] hover:border-[color:var(--mano-primary)]/40 p-3 flex items-center gap-3 transition-colors"
            >
              <div className="h-9 w-9 rounded-lg bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] flex items-center justify-center shrink-0">
                <Icon size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{t.title}</div>
                <div className="text-[11px] text-[color:var(--mano-ink-soft)] truncate">
                  {t.category_name}{t.subcategory ? " · " + t.subcategory : ""} · {MODE_LABELS[t.mode]}{t.duration_min ? " · " + t.duration_min + "m" : ""}
                </div>
              </div>
              <ChevronRight size={14} className="text-[color:var(--mano-ink-soft)] shrink-0" />
            </button>
          );
        })}
      </div>
    </section>
  );
}
